import React, { useEffect, useRef, useState, useCallback } from 'react';

export interface VenueData {
  name: string;
  isCustom: boolean;
  placeId?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  zip?: string;
  fullAddress?: string;
  location?: {
    lat: number;
    lng: number;
  };
  phone?: string;
  website?: string;
  placeDetails?: google.maps.places.PlaceResult;
}

interface VenueAutocompleteProps {
  value: string;
  onChange: (venueData: VenueData | null) => void;
  placeholder?: string;
  className?: string;
}

interface PredictionItem {
  placeId: string;
  mainText: string;
  secondaryText: string;
}

const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' }
];

const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' }
];

const CA_PROVINCES = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' }
];

const VenueAutocomplete: React.FC<VenueAutocompleteProps> = ({
  value,
  onChange,
  placeholder = "Search for your venue",
  className = ""
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const addressAutocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [internalValue, setInternalValue] = useState(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Dropdown state
  const [predictions, setPredictions] = useState<PredictionItem[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Custom venue mode state
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [customVenueName, setCustomVenueName] = useState('');
  const [customAddress, setCustomAddress] = useState('');
  const [customCity, setCustomCity] = useState('');
  const [customState, setCustomState] = useState('');
  const [customCountry, setCustomCountry] = useState('US');
  const [addressSelected, setAddressSelected] = useState(false);
  const [addressPlaceDetails, setAddressPlaceDetails] = useState<google.maps.places.PlaceResult | null>(null);

  const GOOGLE_MAPS_API_KEY = 'AIzaSyBxt7GTz4w74LxqVtx31Ert13X1EmG3hhk';

  // Sync internal value when external value changes
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // Load Google Maps script
  useEffect(() => {
    const loadGoogleMapsScript = () => {
      if (window.google && window.google.maps) {
        setIsLoaded(true);
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setIsLoaded(true);
      };
      document.head.appendChild(script);
    };

    loadGoogleMapsScript();
  }, []);

  // Initialize services
  useEffect(() => {
    if (!isLoaded) return;

    autocompleteServiceRef.current = new google.maps.places.AutocompleteService();

    // Create a dummy div for PlacesService (required by the API)
    const dummyDiv = document.createElement('div');
    placesServiceRef.current = new google.maps.places.PlacesService(dummyDiv);
  }, [isLoaded]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch predictions when input changes
  useEffect(() => {
    if (!isLoaded || !autocompleteServiceRef.current || isCustomMode) return;

    if (internalValue.length < 3) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    const request: google.maps.places.AutocompletionRequest = {
      input: internalValue,
      types: ['establishment']
    };

    autocompleteServiceRef.current.getPlacePredictions(request, (results, status) => {
      if (status === google.maps.places.PlacesServiceStatus.OK && results) {
        const items: PredictionItem[] = results.map(prediction => ({
          placeId: prediction.place_id,
          mainText: prediction.structured_formatting.main_text,
          secondaryText: prediction.structured_formatting.secondary_text
        }));
        setPredictions(items);
        setShowDropdown(true);
      } else {
        setPredictions([]);
        setShowDropdown(internalValue.length >= 3); // Still show dropdown for "not listed" option
      }
    });
  }, [internalValue, isLoaded, isCustomMode]);

  // Initialize address autocomplete for custom mode
  useEffect(() => {
    if (!isLoaded || !addressInputRef.current || !isCustomMode) return;

    // Clean up previous instance
    if (addressAutocompleteRef.current) {
      google.maps.event.clearInstanceListeners(addressAutocompleteRef.current);
    }

    addressAutocompleteRef.current = new google.maps.places.Autocomplete(addressInputRef.current, {
      types: ['address'],
      componentRestrictions: { country: ['us', 'ca'] },
      fields: ['formatted_address', 'geometry', 'place_id', 'address_components']
    });

    addressAutocompleteRef.current.addListener('place_changed', () => {
      const place = addressAutocompleteRef.current?.getPlace();

      if (place && place.formatted_address) {
        setCustomAddress(place.formatted_address);
        setAddressPlaceDetails(place);
        setAddressSelected(true);

        // Extract address components
        const addressComponents = place.address_components || [];
        let city = '', state = '', country = '';

        addressComponents.forEach(component => {
          const types = component.types;
          if (types.includes('locality')) {
            city = component.long_name;
          } else if (types.includes('administrative_area_level_1')) {
            state = component.short_name;
          } else if (types.includes('country')) {
            country = component.short_name;
          }
        });

        setCustomCity(city);
        setCustomState(state);
        if (country === 'US' || country === 'CA') {
          setCustomCountry(country);
        }

        // Update the parent with current data
        updateCustomVenueData(customVenueName, place.formatted_address, city, state, country, place);
      }
    });

    return () => {
      if (addressAutocompleteRef.current) {
        google.maps.event.clearInstanceListeners(addressAutocompleteRef.current);
      }
    };
  }, [isLoaded, isCustomMode, customVenueName]);

  const updateCustomVenueData = useCallback((
    name: string,
    address: string,
    city: string,
    state: string,
    country: string,
    placeDetails?: google.maps.places.PlaceResult | null
  ) => {
    // Only report valid data if we have required fields
    if (name.trim() && city.trim() && state.trim() && country.trim()) {
      let zip = '';
      const fullAddress = address;
      let location: { lat: number; lng: number } | undefined = undefined;

      if (placeDetails) {
        const addressComponents = placeDetails.address_components || [];
        addressComponents.forEach(component => {
          if (component.types.includes('postal_code')) {
            zip = component.long_name;
          }
        });
        const lat = placeDetails.geometry?.location?.lat();
        const lng = placeDetails.geometry?.location?.lng();
        if (lat !== undefined && lng !== undefined) {
          location = { lat, lng };
        }
      }

      onChangeRef.current({
        name: name.trim(),
        isCustom: true,
        address: address.trim(),
        city: city.trim(),
        state: state.trim(),
        country: country.trim(),
        zip,
        fullAddress,
        location
      });
    } else {
      onChangeRef.current(null);
    }
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    setHighlightedIndex(-1);
    onChangeRef.current(null);
  }, []);

  const handleInputFocus = () => {
    if (internalValue.length >= 3) {
      setShowDropdown(true);
    }
  };

  const selectPlace = (placeId: string) => {
    if (!placesServiceRef.current) return;

    placesServiceRef.current.getDetails(
      {
        placeId,
        fields: ['formatted_address', 'geometry', 'place_id', 'name', 'business_status', 'types', 'address_components', 'formatted_phone_number', 'website']
      },
      (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place) {
          const displayName = place.name || place.formatted_address || '';
          setInternalValue(displayName);
          setShowDropdown(false);
          setPredictions([]);

          // Extract address components
          const addressComponents = place.address_components || [];
          let address = '', city = '', state = '', zip = '', country = '';

          addressComponents.forEach(component => {
            const types = component.types;
            if (types.includes('street_number') || types.includes('route')) {
              address += component.long_name + ' ';
            } else if (types.includes('locality')) {
              city = component.long_name;
            } else if (types.includes('administrative_area_level_1')) {
              state = component.short_name;
            } else if (types.includes('postal_code')) {
              zip = component.long_name;
            } else if (types.includes('country')) {
              country = component.long_name;
            }
          });

          // Extract location from geometry
          const lat = place.geometry?.location?.lat();
          const lng = place.geometry?.location?.lng();

          if (lat === undefined || lng === undefined) {
            console.warn('VenueAutocomplete: No geometry returned from Places API for place:', place.place_id, place.name);
          }

          onChangeRef.current({
            name: displayName,
            isCustom: false,
            placeId: place.place_id,
            address: address.trim(),
            city,
            state,
            zip,
            country,
            fullAddress: place.formatted_address || '',
            location: (lat !== undefined && lng !== undefined) ? { lat, lng } : undefined,
            phone: place.formatted_phone_number || '',
            website: place.website || '',
            placeDetails: place
          });
        }
      }
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const totalItems = predictions.length + 1; // +1 for "not listed" option

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev + 1) % totalItems);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev - 1 + totalItems) % totalItems);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < predictions.length) {
        selectPlace(predictions[highlightedIndex].placeId);
      } else if (highlightedIndex === predictions.length) {
        handleCustomModeClick();
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const handleCustomModeClick = () => {
    setIsCustomMode(true);
    setCustomVenueName(internalValue); // Preserve any text they've already typed
    setShowDropdown(false);
    setPredictions([]);
    onChangeRef.current(null);
  };

  const handleBackToSearch = () => {
    setIsCustomMode(false);
    setCustomVenueName('');
    setCustomAddress('');
    setCustomCity('');
    setCustomState('');
    setCustomCountry('US');
    setAddressSelected(false);
    setAddressPlaceDetails(null);
    setInternalValue('');
    onChangeRef.current(null);
  };

  const handleCustomVenueNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setCustomVenueName(name);
    updateCustomVenueData(name, customAddress, customCity, customState, customCountry, addressPlaceDetails);
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomAddress(e.target.value);
    setAddressSelected(false);
  };

  const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const city = e.target.value;
    setCustomCity(city);
    updateCustomVenueData(customVenueName, customAddress, city, customState, customCountry, addressPlaceDetails);
  };

  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const state = e.target.value;
    setCustomState(state);
    updateCustomVenueData(customVenueName, customAddress, customCity, state, customCountry, addressPlaceDetails);
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const country = e.target.value;
    setCustomCountry(country);
    setCustomState(''); // Reset state when country changes
    updateCustomVenueData(customVenueName, customAddress, customCity, '', country, addressPlaceDetails);
  };

  const getStatesOrProvinces = () => {
    return customCountry === 'CA' ? CA_PROVINCES : US_STATES;
  };

  // Custom mode UI
  if (isCustomMode) {
    return (
      <div className="space-y-4">
        <div>
          <input
            type="text"
            value={customVenueName}
            onChange={handleCustomVenueNameChange}
            placeholder="Enter your venue name"
            className={className}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Address <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            ref={addressInputRef}
            type="text"
            value={customAddress}
            onChange={handleAddressChange}
            placeholder="Search for address or enter manually"
            className={className}
            autoComplete="off"
          />
          {addressSelected && (
            <p className="mt-1 text-sm text-green-600">
              ✓ Address selected
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              City <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={customCity}
              onChange={handleCityChange}
              placeholder="City"
              className={className}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              State/Province <span className="text-red-500">*</span>
            </label>
            <select
              value={customState}
              onChange={handleStateChange}
              className={className}
            >
              <option value="">Select {customCountry === 'CA' ? 'province' : 'state'}</option>
              {getStatesOrProvinces().map(s => (
                <option key={s.code} value={s.code}>{s.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Country <span className="text-red-500">*</span>
          </label>
          <select
            value={customCountry}
            onChange={handleCountryChange}
            className={className}
          >
            {COUNTRIES.map(c => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={handleBackToSearch}
          className="text-sm text-gray-600 hover:text-gray-800 underline"
        >
          ← Back to venue search
        </button>
      </div>
    );
  }

  // Standard venue search UI with custom dropdown
  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={internalValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
      />

      {showDropdown && internalValue.length >= 3 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {predictions.map((prediction, index) => (
            <div
              key={prediction.placeId}
              onClick={() => selectPlace(prediction.placeId)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`px-4 py-2 cursor-pointer ${
                highlightedIndex === index ? 'bg-gray-100' : ''
              }`}
            >
              <div className="font-medium text-gray-900">{prediction.mainText}</div>
              <div className="text-sm text-gray-500">{prediction.secondaryText}</div>
            </div>
          ))}

          {/* "My venue isn't listed" option - always at the bottom */}
          <div
            onClick={handleCustomModeClick}
            onMouseEnter={() => setHighlightedIndex(predictions.length)}
            className={`px-4 py-3 cursor-pointer border-t border-gray-200 ${
              highlightedIndex === predictions.length ? 'bg-gray-100' : ''
            }`}
          >
            <div className="text-customTeal3 font-medium">My venue isn&apos;t listed</div>
            <div className="text-sm text-gray-500">Enter venue details manually</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VenueAutocomplete;
